import * as React from "react";

import { cn } from "../lib/utils";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "./ui";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "./ui";

type ResponsiveSidebarProps = {
  trigger?: React.ReactNode;
  triggerLabel?: string;
  title?: React.ReactNode;
  side?: "left" | "right";
  children: React.ReactNode;
  footer?: React.ReactNode;
  mobileFooter?: React.ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  desktopContentClassName?: string;
  mobileContentClassName?: string;
  headerClassName?: string;
  bodyClassName?: string;
};

export function ResponsiveSidebar({
  trigger,
  triggerLabel = "Open Panel",
  title,
  side = "right",
  children,
  footer,
  open,
  defaultOpen = false,
  onOpenChange,
  desktopContentClassName,
  mobileContentClassName,
  headerClassName,
  bodyClassName,
}: ResponsiveSidebarProps) {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
  const resolvedOpen = open ?? internalOpen;
  const handleOpenChange = onOpenChange ?? setInternalOpen;
  const isDesktop = typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches;
  const resolvedTitle = title ?? "Tune settings";

  const triggerNode = trigger ?? <button>{triggerLabel}</button>;

  if (isDesktop) {
    return (
      <Sheet open={resolvedOpen} onOpenChange={handleOpenChange}>
        <SheetTrigger asChild>{triggerNode}</SheetTrigger>
        <SheetContent side={side} className={desktopContentClassName}>
          <SheetHeader className={headerClassName}>
            <SheetTitle className={"bg-gray-100 m-0 py-6 text-center"}>{resolvedTitle}</SheetTitle>
          </SheetHeader>

          <div className={cn("flex-1 overflow-y-auto", bodyClassName)}>{children}</div>

          {footer && <SheetFooter>{footer}</SheetFooter>}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Drawer open={resolvedOpen} onOpenChange={handleOpenChange}>
      <DrawerTrigger asChild>{triggerNode}</DrawerTrigger>
      <DrawerContent className={mobileContentClassName}>
        <DrawerHeader className={cn(headerClassName)}>
          <DrawerTitle className={"bg-gray-100 py-6 text-center"}>{resolvedTitle}</DrawerTitle>
        </DrawerHeader>

        <div className={cn(bodyClassName)}>{children}</div>
      </DrawerContent>
    </Drawer>
  );
}

type ResponsiveSidebarConfig = Partial<Omit<ResponsiveSidebarProps, "children">>;

export function withResponsiveSidebar<P>(
  Component: React.ComponentType<P>,
  defaults: ResponsiveSidebarConfig = {}
) {
  const Wrapped = (props: P & ResponsiveSidebarConfig) => {
    const ComponentEl = Component as React.ComponentType<any>;
    const {
      trigger,
      triggerLabel,
      title,
      side,
      footer,
      mobileFooter,
      open,
      defaultOpen,
      onOpenChange,
      desktopContentClassName,
      mobileContentClassName,
      headerClassName,
      bodyClassName,
      ...componentProps
    } = props;

    return (
      <ResponsiveSidebar
        trigger={trigger ?? defaults.trigger}
        triggerLabel={triggerLabel ?? defaults.triggerLabel}
        title={title ?? defaults.title}
        side={side ?? defaults.side}
        footer={footer ?? defaults.footer}
        mobileFooter={mobileFooter ?? defaults.mobileFooter}
        open={open ?? defaults.open}
        defaultOpen={defaultOpen ?? defaults.defaultOpen}
        onOpenChange={onOpenChange ?? defaults.onOpenChange}
        desktopContentClassName={desktopContentClassName ?? defaults.desktopContentClassName}
        mobileContentClassName={mobileContentClassName ?? defaults.mobileContentClassName}
        headerClassName={headerClassName ?? defaults.headerClassName}
        bodyClassName={bodyClassName ?? defaults.bodyClassName}
      >
        {React.createElement(ComponentEl, componentProps as P)}
      </ResponsiveSidebar>
    );
  };

  Wrapped.displayName = `WithResponsiveSidebar(${Component.displayName || Component.name || "Component"})`;

  return Wrapped;
}
